import { createForm } from '@seek/forms-ui';
import { useTranslations } from '@vocab/react';
import { Box, Stack } from 'braid-design-system';
import React, { useState } from 'react';

import { getQueryString } from 'src/shared/helpers';
import { useConfig } from 'src/shared/hooks';
import type { Language } from 'src/shared/types';

import { Form, FormIntro, ThanksView } from './components';
import translations from './components/SubjectData/.vocab';

const FORM_NAME = 'hirer-contact-us-form';

export type DynamicFormValues = Record<string, any>;

const { FormProvider, useField } = createForm<DynamicFormValues, any, Language>(
  FORM_NAME,
);
export { useField };

const EnquiryForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const { locale, language } = useConfig();
  const enquiryQuery =
    locale === 'AU' || locale === 'NZ' ? '' : getQueryString('enquiry');

  const { t } = useTranslations(translations);

  let validQuery = false;

  const getSubjectReason = () => {
    switch (enquiryQuery) {
      case 'talent-search':
        validQuery = true;
        return {
          subject: t('Products and pricing'),
          reason: t('Talent Search'),
          additionalInformation: 'I need help with finding candidates.',
        };
      case 'discounts':
        validQuery = true;
        return {
          subject: t('Products and pricing'),
          reason: t('Other'),
          additionalInformation: 'I need help with bulk discounts.',
        };
      case 'branding':
        validQuery = true;
        return {
          subject: t('Products and pricing'),
          reason: t('Branding opportunity'),
          additionalInformation: 'I need help with branding.',
        };
      case 'job-ads':
        validQuery = true;
        return {
          subject: t('Products and pricing'),
          reason: t('Job Advertisements'),
          additionalInformation: 'I need help with job ads.',
        };
      case 'general':
        validQuery = true;
        return {
          subject: t('Talent Sourcing Advice'),
          reason: t('Need help hiring'),
          additionalInformation: 'I need help with hiring.',
        };
      case 'page-not-found':
        validQuery = true;
        return {
          subject: t('Technical Issues'),
          reason: t('Other'),
          additionalInformation: '',
        };
      case 'page-load-failed':
        validQuery = true;
        return {
          subject: t('Technical Issues'),
          reason: t('Other'),
          additionalInformation: '',
        };
      default:
        return {
          subject: '',
          reason: '',
          additionalInformation: '',
        };
    }
  };

  const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    telephone: '',
    company: '',
    preferredContactMethod: 'Any',
    preferredContactTime: 'Any',
    rspRelatedProducts: [],
    rspActions: [],
    rspPhone: '',
    rspEmail: '',
    rspName: '',
    ...getSubjectReason(),
  };

  return (
    <Box>
      <Box paddingX="gutter">
        {submitted ? (
          <ThanksView setSubmitted={setSubmitted} /> //eslint-disable-line
        ) : (
          <Stack space="xlarge">
            <FormIntro />
            <FormProvider initialValues={initialValues} language={language}>
              {({ handleSubmit, resetForm, setFieldValue }) => (
                <Form
                  querystring={validQuery}
                  handleSubmit={handleSubmit}
                  setSubmitted={setSubmitted}
                  setFieldValue={setFieldValue}
                  resetForm={resetForm}
                />
              )}
            </FormProvider>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default EnquiryForm;
